import { EnvironmentType } from '@Builder/dto/internal/env';
import { fromSpecificToShallowType } from '@Builder/utils/Validation';
import { createContext, useContext, useEffect, useState } from 'react';
import useEditorContextHook from './hooks/useEditorContext';
import useDrawerContext from './hooks/useOverlayHook';
import { jsx as _jsx } from "react/jsx-runtime";
var BuilderContext = /*#__PURE__*/createContext(undefined);

function BuilderContextProvider(_ref) {
  var children = _ref.children;

  // App State
  var _useState = useState(undefined),
      activityId = _useState[0],
      setActivityId = _useState[1];

  var _useState2 = useState(undefined),
      currentLogicFlowId = _useState2[0],
      setCurrentLogicFlowId = _useState2[1];

  var _useState3 = useState({}),
      currentUser = _useState3[0],
      setCurrentUser = _useState3[1];

  var _useState4 = useState([]),
      currentUsersEditing = _useState4[0],
      setCurrentUsersEditing = _useState4[1];

  var _useState5 = useState(EnvironmentType.DEVELOPMENT),
      env = _useState5[0],
      setEnvironment = _useState5[1];

  var _useState6 = useState(undefined),
      flowDiagram = _useState6[0],
      setFlowDiagram = _useState6[1];

  var _useState7 = useState(undefined),
      selectedNode = _useState7[0],
      setSelectedNode = _useState7[1];

  var _useState8 = useState(undefined),
      selectedStep = _useState8[0],
      setSelectedStep = _useState8[1];

  var _useState9 = useState(true),
      editable = _useState9[0],
      setEditable = _useState9[1];

  var EditorContext = useEditorContextHook(); // UI State
  // Editor State

  var _useState10 = useState(false),
      showEditor = _useState10[0],
      setShowEditor = _useState10[1]; // Dialog State


  var _useState11 = useState(false),
      confirmationDialogIsOpen = _useState11[0],
      setConfirmationDialogIsOpen = _useState11[1];

  var _useState12 = useState(false),
      deployModalDialogIsOpen = _useState12[0],
      setDeployModalDialogIsOpen = _useState12[1];

  var _useState13 = useState({}),
      alertDialog = _useState13[0],
      setAlertDialog = _useState13[1]; // Modal State


  var _useDrawerContext = useDrawerContext(),
      closeModal = _useDrawerContext.closeOverlay,
      currentModalKey = _useDrawerContext.currentOverlayKey,
      openModal = _useDrawerContext.openOverlay,
      toggleModal = _useDrawerContext.toggleOverlay; // Drawer State


  var _useDrawerContext2 = useDrawerContext(),
      closeDrawer = _useDrawerContext2.closeOverlay,
      currentDrawerKey = _useDrawerContext2.currentOverlayKey,
      openDrawer = _useDrawerContext2.openOverlay,
      toggleDrawer = _useDrawerContext2.toggleOverlay;

  function closeEditorTabs(environment, callback) {
    var action = function action() {
      EditorContext.clearEditor();
      setCurrentUsersEditing(function (current) {
        return current.filter(function (element) {
          return fromSpecificToShallowType(element.id) !== fromSpecificToShallowType(currentUser._id);
        });
      });
      callback === null || callback === void 0 ? void 0 : callback();

      if (environment && environment !== env) {
        setEnvironment(environment);
      }
    };

    var hasChanges = EditorContext.changesPending();

    if (!hasChanges) {
      action();
    } else if (showEditor) {
      setAlertDialog({
        title: 'Close editor',
        description: 'You have changes pending. Closing will lose all changes. Are you sure you want to close?',
        cancelText: 'Cancel',
        confirmText: 'Close anyway',
        destructiveOnClose: function destructiveOnClose() {
          action();
        }
      });
      setConfirmationDialogIsOpen(true);
    }
  }

  function setEnv(environment) {
    if (environment === env) {
      return;
    }

    closeEditorTabs(environment);
  }

  function setOpenVersionControlDrawer(isVcDrawerOpen) {
    if (isVcDrawerOpen) {
      setEditable(false);
      closeEditorTabs();
    } else {
      setEditable(true);
    }
  } // Use Effects
  // Update env for EditorContext when it changes:


  useEffect(function () {
    EditorContext.setFlowEnvironment(env);
  }, [env]);
  useEffect(function () {
    EditorContext.setActivityId(activityId);
  }, [activityId]);
  useEffect(function () {
    var haveEditorData = (EditorContext === null || EditorContext === void 0 ? void 0 : EditorContext.editorData) && Object.keys(EditorContext === null || EditorContext === void 0 ? void 0 : EditorContext.editorData).length > 0;
    setShowEditor(haveEditorData);
  }, [EditorContext.editorData]);
  useEffect(function () {
    setEditable(env === EnvironmentType.DEVELOPMENT || currentDrawerKey === 'VERSION_DRAWER');
  }, [env]);
  return /*#__PURE__*/_jsx(BuilderContext.Provider, {
    value: {
      activityId: activityId,
      currentLogicFlowId: currentLogicFlowId,
      currentUser: currentUser,
      currentUsersEditing: currentUsersEditing,
      EditorContext: EditorContext,
      env: env,
      flowDiagram: flowDiagram,
      selectedNode: selectedNode,
      selectedStep: selectedStep,
      setCurrentLogicFlowId: setCurrentLogicFlowId,
      setCurrentUser: setCurrentUser,
      setCurrentUsersEditing: setCurrentUsersEditing,
      setEnv: setEnv,
      setFlowDiagram: setFlowDiagram,
      setSelectedNode: setSelectedNode,
      setSelectedStep: setSelectedStep,
      // UI State
      editable: editable,
      showEditor: showEditor,
      closeEditorTabs: closeEditorTabs,
      // Dialogs
      alertDialog: alertDialog,
      confirmationDialogIsOpen: confirmationDialogIsOpen,
      deployModalDialogIsOpen: deployModalDialogIsOpen,
      setActivityId: setActivityId,
      setAlertDialog: setAlertDialog,
      setConfirmationDialogIsOpen: setConfirmationDialogIsOpen,
      setDeployModalDialogIsOpen: setDeployModalDialogIsOpen,
      // Modals
      currentModalKey: currentModalKey,
      openModal: openModal,
      closeModal: closeModal,
      toggleModal: toggleModal,
      // Drawers
      currentDrawerKey: currentDrawerKey,
      openDrawer: openDrawer,
      closeDrawer: closeDrawer,
      toggleDrawer: toggleDrawer,
      setOpenVersionControlDrawer: setOpenVersionControlDrawer
    },
    children: children
  });
}

export function useBuilderContext() {
  return useContext(BuilderContext);
}
export default BuilderContextProvider;